import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IFilterSettlementListResponse, ISettlementItem } from '../core.interface';
import { Spinner } from './spinner';
const HOME_SETTLEMENTS_PREFETCH_SIZE = 24;
const HOME_TABLE_PAGE_SIZE = 10;

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private chartSettlementsSignal = signal<ISettlementItem[]>([]);
  public chartSettlements = this.chartSettlementsSignal.asReadonly();
  private paginatedSettlementsSignal = signal<ISettlementItem[]>([]);
  private $spinner = inject(Spinner);
  public paginatedSettlements = this.paginatedSettlementsSignal.asReadonly();

  chartSettlementsByDateSignal = signal<ISettlementItem[]>([]);
  public chartSettlementsByDate = this.chartSettlementsByDateSignal.asReadonly();

  private allSettlementsSignal = signal<ISettlementItem[]>([]);
  public allSettlements = this.allSettlementsSignal.asReadonly();

  /** Última liquidación del gráfico (array en orden cronológico ASC). */
  public lastSettlement = computed(() => {
    const settlements = this.chartSettlementsSignal();
    if (settlements.length === 0) return null;
    return settlements[settlements.length - 1] ?? null;
  });

  public hasMore = true;
  private isFetchingMore = false;

  private totalElementsSignal = signal(0);
  private totalPagesSignal = signal(0);

  private tableTenantIdSignal = signal<string | null>(null);
  private chartTenantIdSignal = signal<string | null>(null);
  private tableDateFromSignal = signal<string | null>(null);
  private tableDateToSignal = signal<string | null>(null);
  private tablePageSignal = signal(0);
  private tableSizeSignal = signal(10);
  private chartDateFromSignal = signal<string | null>(null);
  private chartDateToSignal = signal<string | null>(null);

  private homePrefetchPromise: Promise<void> | null = null;
  private homePrefetchTenantId: string | null = null;

  public totalElements = this.totalElementsSignal.asReadonly();
  public totalPages = this.totalPagesSignal.asReadonly();
  public tablePage = this.tablePageSignal.asReadonly();

  constructor(private http: HttpClient) {}

  public isTableForTenant(tenantId: string, from?: string, to?: string): boolean {
    if (this.tableTenantIdSignal() !== tenantId) return false;
    if (from === undefined || to === undefined) return true;
    return this.tableDateFromSignal() === from && this.tableDateToSignal() === to;
  }

  /**
   * Cache reutilizable al entrar en Home/Liquidaciones: mismo tenant/rango y página 0.
   * Evita pintar filas de página N con el paginador en 1.
   */
  public isReusableTableCache(tenantId: string, from?: string, to?: string): boolean {
    return this.isTableForTenant(tenantId, from, to) && this.tablePageSignal() === 0;
  }

  public isChartForTenant(tenantId: string, from?: string, to?: string): boolean {
    if (this.chartTenantIdSignal() !== tenantId) return false;
    if (from === undefined || to === undefined) return true;
    return this.chartDateFromSignal() === from && this.chartDateToSignal() === to;
  }

  getDefaultSettlementsDateRange(): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    from.setFullYear(to.getFullYear() - 2);
    return { from: this.formatDate(from), to: this.formatDate(to) };
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** Precarga gráfica + tabla de liquidaciones (24 meses, defaults del componente). */
  async prefetchSettlementsPage(tenantId: string): Promise<void> {
    await this.prefetchHomeSettlements(tenantId);
  }

  /**
   * Una sola petición para Home: tabla (página 0) + gráfico (hasta 6 barras del suministro activo).
   */
  public prefetchHomeSettlements(tenantId: string): Promise<void> {
    const { from, to } = this.getDefaultSettlementsDateRange();
    const tableCached = this.isReusableTableCache(tenantId, from, to);
    const chartCached =
      this.isChartForTenant(tenantId, from, to) && this.chartSettlementsSignal().length > 0;

    if (tableCached && chartCached) {
      return Promise.resolve();
    }

    if (this.homePrefetchPromise && this.homePrefetchTenantId === tenantId) {
      return this.homePrefetchPromise;
    }

    this.homePrefetchTenantId = tenantId;
    this.homePrefetchPromise = this.fetchHomeSettlementsPrefetch(tenantId, from, to).finally(() => {
      if (this.homePrefetchTenantId === tenantId) {
        this.homePrefetchPromise = null;
      }
    });

    return this.homePrefetchPromise;
  }

  public clear(): void {
    this.chartSettlementsSignal.set([]);
    this.paginatedSettlementsSignal.set([]);
    this.chartSettlementsByDateSignal.set([]);
    this.allSettlementsSignal.set([]);
    this.totalElementsSignal.set(0);
    this.totalPagesSignal.set(0);
    this.tableTenantIdSignal.set(null);
    this.chartTenantIdSignal.set(null);
    this.tableDateFromSignal.set(null);
    this.tableDateToSignal.set(null);
    this.tablePageSignal.set(0);
    this.tableSizeSignal.set(10);
    this.chartDateFromSignal.set(null);
    this.chartDateToSignal.set(null);
    this.homePrefetchPromise = null;
    this.homePrefetchTenantId = null;
  }

  private async fetchHomeSettlementsPrefetch(
    tenantId: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<void> {
    const params = new HttpParams()
      .set('from', dateFrom)
      .set('to', dateTo)
      .set('page', '0')
      .set('size', HOME_SETTLEMENTS_PREFETCH_SIZE.toString())
      .set('sort', 'invoiceDate,desc');

    try {
      const res = await firstValueFrom(
        this.http.get<IFilterSettlementListResponse>(
          `${environment.apiUrl}/tenant/${tenantId}/settlements`,
          { params },
        ),
      );

      this.applyHomeSettlementsPrefetch(
        tenantId,
        dateFrom,
        dateTo,
        res.data.content,
        res.data.totalElements,
        res.data.totalPages,
      );
    } catch (err) {
      this.chartSettlementsSignal.set([]);
      this.paginatedSettlementsSignal.set([]);
      console.error(err);
    }
  }

  private applyHomeSettlementsPrefetch(
    tenantId: string,
    dateFrom: string,
    dateTo: string,
    content: ISettlementItem[],
    totalElements: number,
    totalPages: number,
  ): void {
    const descending = [...content];

    this.paginatedSettlementsSignal.set(descending.slice(0, HOME_TABLE_PAGE_SIZE));
    this.totalElementsSignal.set(totalElements);
    this.totalPagesSignal.set(totalPages);
    this.tableTenantIdSignal.set(tenantId);
    this.tableDateFromSignal.set(dateFrom);
    this.tableDateToSignal.set(dateTo);
    this.tablePageSignal.set(0);
    this.tableSizeSignal.set(HOME_TABLE_PAGE_SIZE);

    const chronological = [...descending].reverse();
    this.allSettlementsSignal.set(chronological);
    this.chartSettlementsByDateSignal.set(chronological);
    this.chartTenantIdSignal.set(tenantId);
    this.chartDateFromSignal.set(dateFrom);
    this.chartDateToSignal.set(dateTo);
    this.populateLastSettlementsForChart(descending);
  }

  public async fetchLastSettlementsForChart(tenantId: string) {
    const { from, to } = this.getDefaultSettlementsDateRange();

    if (this.isChartForTenant(tenantId, from, to) && this.chartSettlementsSignal().length > 0) {
      return;
    }

    await this.prefetchHomeSettlements(tenantId);
  }

  /** Espera liquidaciones en orden desc (más reciente primero). Deja el signal en ASC para el gráfico. */
  private populateLastSettlementsForChart(settlementsDesc: ISettlementItem[]): void {
    const newest = settlementsDesc[0];
    if (!newest) {
      this.chartSettlementsSignal.set([]);
      return;
    }

    const chronological = [...settlementsDesc]
      .filter((settlement) => settlement.supplyType === newest.supplyType)
      .reverse();

    this.chartSettlementsSignal.set(chronological.slice(-6));
  }

  private resolveSettlementsDateRange(
    from: string | null | undefined,
    to: string | null | undefined,
  ): { from: string; to: string } {
    const defaults = this.getDefaultSettlementsDateRange();
    return {
      from: from?.trim() ? from : defaults.from,
      to: to?.trim() ? to : defaults.to,
    };
  }

  public async fetchSettlementsForChart(
    tenantId: string,
    from: string,
    to: string,
    options: { manageSpinner?: boolean } = {},
  ) {
    const manageSpinner = options.manageSpinner ?? true;

    if (manageSpinner) {
      this.$spinner.show();
    }

    const { from: dateFrom, to: dateTo } = this.resolveSettlementsDateRange(from, to);
    const params = new HttpParams()
      .set('from', dateFrom)
      .set('to', dateTo)
      .set('page', '0')
      .set('sort', 'invoiceDate,desc');

    try {
      const res = await firstValueFrom(
        this.http.get<IFilterSettlementListResponse>(
          `${environment.apiUrl}/tenant/${tenantId}/settlements`,
          { params },
        ),
      );

      // API llega en desc; no mutar content — populateLastSettlementsForChart espera desc.
      const descending = [...res.data.content];
      const data = [...descending].reverse();

      this.allSettlementsSignal.set(data);
      this.chartSettlementsByDateSignal.set(data);
      this.chartTenantIdSignal.set(tenantId);
      this.chartDateFromSignal.set(dateFrom);
      this.chartDateToSignal.set(dateTo);
      this.populateLastSettlementsForChart(descending);
    } catch (err) {
      this.chartSettlementsByDateSignal.set([]);
      console.error(err);
    } finally {
      if (manageSpinner) {
        this.$spinner.hide();
      }
    }
  }
  public filterChartBySupplyType(supplyType: string | null) {
    const all = this.allSettlementsSignal();

    if (!supplyType) {
      this.chartSettlementsByDateSignal.set(all);
      return;
    }

    const filtered = all.filter((x) => x.supplyType === supplyType);

    this.chartSettlementsByDateSignal.set(filtered);
  }
  public getGroupedSupplyTypes(): string[] {
    const data = this.allSettlementsSignal();

    const unique = [...new Set(data.map((x) => x.supplyType))];

    return unique.filter(Boolean);
  }
  public async fetchSettlementsPaginated(
    tenantId: string,
    from: string | null,
    to: string | null,
    page: number = 0,
    size: number = 10,
    sortField: string = 'invoiceDate',
    sort: string = 'desc',
    manageSpinner = true,
  ): Promise<void> {
    this.isFetchingMore = true;

    const { from: dateFrom, to: dateTo } = this.resolveSettlementsDateRange(from, to);
    const params = new HttpParams()
      .set('from', dateFrom)
      .set('to', dateTo)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', `${sortField},${sort}`);

    try {
      const res = await firstValueFrom(
        this.http.get<IFilterSettlementListResponse>(
          `${environment.apiUrl}/tenant/${tenantId}/settlements`,
          { params },
        ),
      );

      this.paginatedSettlementsSignal.set(res.data.content);
      this.totalElementsSignal.set(res.data.totalElements);
      this.totalPagesSignal.set(res.data.totalPages);
      this.tableTenantIdSignal.set(tenantId);
      this.tableDateFromSignal.set(dateFrom);
      this.tableDateToSignal.set(dateTo);
      this.tablePageSignal.set(page);
      this.tableSizeSignal.set(size);
    } catch (err) {
      console.error(err);
    } finally {
      this.isFetchingMore = false;
      if (manageSpinner) {
        this.$spinner.hide();
      }
    }
  }

  downloadSettlement(tenantId: string, settlementId: number) {
    return this.http.get(
      `${environment.apiUrl}/tenant/${tenantId}/settlements/${settlementId}/stream`,
      { responseType: 'blob' },
    );
  }

  downloadSelectedSettlements(tenantId: string, settlementIds: number[]) {
    return this.http.post(
      `${environment.apiUrl}/tenant/${tenantId}/settlements/download`,
      settlementIds,
      {
        responseType: 'blob',
      },
    );
  }
}
